import { useCallback, useEffect, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'

import { exportMimeForSource, getCroppedImageFile } from '../../lib/cropImage'
import { Button } from './Button'
import { Modal } from './Modal'

/** Allow zooming out so oversized / wide logos can be framed in the crop. */
const MIN_ZOOM = 0.4
const MAX_ZOOM = 3

export type ImageCropDialogProps = {
  open: boolean
  imageUrl: string | null
  /** Source file MIME — used to pick PNG vs JPEG export. */
  sourceMimeType?: string
  aspect: number
  title: string
  description?: string
  /** Prefer PNG export (logos). */
  preferPng?: boolean
  maxEdge?: number
  fileName?: string
  onCancel: () => void
  onConfirm: (file: File) => void
}

export function ImageCropDialog({
  open,
  imageUrl,
  sourceMimeType = 'image/jpeg',
  aspect,
  title,
  description,
  preferPng = false,
  maxEdge = 1920,
  fileName = 'cropped-image',
  onCancel,
  onConfirm,
}: ImageCropDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !imageUrl) return
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    setError(null)
  }, [open, imageUrl])

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels)
  }, [])

  const handleConfirm = async () => {
    if (!imageUrl || !croppedAreaPixels) return
    setBusy(true)
    setError(null)
    try {
      const mimeType = exportMimeForSource(sourceMimeType, preferPng)
      const file = await getCroppedImageFile(imageUrl, croppedAreaPixels, {
        mimeType,
        quality: mimeType === 'image/jpeg' ? 0.92 : undefined,
        maxEdge,
        fileName,
      })
      onConfirm(file)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not crop image')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      title={title}
      size="lg"
      onClose={() => {
        if (!busy) onCancel()
      }}
      footer={
        <>
          <Button type="button" variant="secondary" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={busy || !croppedAreaPixels}
            onClick={() => void handleConfirm()}
          >
            {busy ? 'Preparing…' : 'Use image'}
          </Button>
        </>
      }
    >
      {description ? <p className="mb-3 text-sm text-muted-foreground">{description}</p> : null}

      <div className="relative h-64 w-full overflow-hidden rounded-lg bg-muted sm:h-80">
        {imageUrl ? (
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
            showGrid
            restrictPosition={false}
          />
        ) : null}
      </div>

      <label className="mt-4 block space-y-1.5">
        <span className="text-xs font-medium text-muted-foreground">Zoom</span>
        <input
          type="range"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={0.05}
          value={zoom}
          disabled={busy || !imageUrl}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="w-full accent-primary"
        />
        <span className="block text-[11px] text-muted-foreground">
          Drag left to zoom out if the logo is larger than the frame.
        </span>
      </label>

      {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}
    </Modal>
  )
}
